import Big from 'big.js';
import {combineLatest, BehaviorSubject} from 'rxjs';
import {map, filter, startWith, shareReplay, throttleTime, scan, mergeAll} from 'rxjs/operators';
import {contractUtil} from '@/helper/contract';
import {plus, toString} from '@/helper/num';
import {contractDefaultTakerFeeRateEr$, contractsCache$} from '@/config';
import {track} from '@/et';
import {rasMsg$} from '@/raw/ras';
import {mainUserId$} from '@/raw/users';
import {aopMsgV2$} from '@/raw/aop';
import {totalPosCost$} from '@/positions/calc';
import {wsLogout$} from '@/user/auth-ws';
import type {RiskUnit} from '@/types/ws';

export interface RasAccount {
  totalBalanceRv: string | number;
  totalUsedBalanceRv: string | number;
  totalBonusRv: string;
  isolatedTotalBalanceRv: string | number;
  uid: number;
}

const ras$ = new BehaviorSubject<RiskUnit[]>([]);
const rasMsg2$ = combineLatest([rasMsg$, mainUserId$]).pipe(
  filter(([ras, uid]) => ras?.risk_units[0]?.userID === uid),
  map(([ras]) => ras)
);
const mainRas$ = rasMsg2$.pipe(map((ras) => ras.risk_units));

mainRas$.subscribe(ras$);
wsLogout$.subscribe(() => ras$.next([]));

/**
 * target1阶段：ras需要兼容 classic 模式推送
 * 非uta target1用户：只用到 ras.risk_units 中 contract 数据
 * uta target1用户：用到中所有数据，分 contract 和 margin 账户
 *
 * target2阶段：
 * uta用户: crossAssets 模式推送
 * 普通用户&uta1期用户: classic 模式推送，只用到 ras.risk_units 中 contract 数据，
 * uta target1用户：classic 模式推送，用到中所有数据，分 contract 和 margin 账户
 */
const tradeRas$ = combineLatest([contractsCache$, ras$]).pipe(
  map(([contractsCache, ras]) =>
    ras
      .filter((item) => contractsCache.has(item.symbol) || item.riskMode === 1)
      .map((o) => ({
        ...o,
        symbol: o.riskMode === 4 ? '' : o.symbol,
      }))
  )
);

// riskunit 以 [riskMode, ccy, symbol, posSide] 为 key 的唯一标识
export const riskUnitsCache$ = tradeRas$.pipe(
  map((o) => convertRiskUnit(o)),
  mergeAll(),
  scan((p, c) => p.set((c.symbol || c.valuationCurrency) + c.posSide, c), new Map()),
  startWith(new Map<string, RiskUnit>()),
  shareReplay(1)
);

const riskUnitsCacheByCurrencyGroup$ = riskUnitsCache$.pipe(
  map((o) =>
    Array.from(o.values()).reduce((p, c) => {
      if (p.has(c.valuationCurrency)) {
        return p.set(c.valuationCurrency, [...p.get(c.valuationCurrency), c]);
      }
      return p.set(c.valuationCurrency, [c]);
    }, new Map<string, RiskUnit[]>())
  ),
  shareReplay(1)
);

export const rasAccountCache$ = combineLatest([riskUnitsCacheByCurrencyGroup$, totalPosCost$]).pipe(
  map(([o, totalPosCost]): Map<string, RasAccount> => calcRasBalance2(o, totalPosCost)),
  shareReplay(1)
);

function calcRasBalance2(riskUnitsCache: Map<string, RiskUnit[]>, posCostRv?: Map<string, Big>) {
  return Array.from(riskUnitsCache.keys()).reduce((p, key) => {
    return p.set(key, calcRasBalance(riskUnitsCache.get(key), key, posCostRv.get(key) || Big(0) || null));
  }, new Map());
}

export function calcRasBalance(data: RiskUnit[], currency0?: string, posCostRv?: Big): RasAccount {
  const totalBalanceRv = data.reduce(
    (p, {totalBalanceRv = '0', valuationCurrency}) => (valuationCurrency === currency0 ? plus(p, totalBalanceRv) : p),
    0
  );

  const isolatedTotalBalanceRv = data
    .filter(({symbol}) => symbol)
    .reduce(
      (p, {totalBalanceRv = '0', valuationCurrency}) => (valuationCurrency === currency0 ? plus(p, totalBalanceRv) : p),
      0
    );
  const totalUsedBalanceRv = data
    .filter(({symbol}) => !symbol)
    .reduce(
      (
        p,
        {
          symbol,
          totalPosCostRv = '0',
          totalDebtCostRv = '0',
          totalOrdUsedBalanceRv = '0',
          totalOrdOpenLossRv = '0',
          fixedUsedRv = '0',
        }
      ) => {
        const posCost = !symbol && posCostRv ? posCostRv : totalPosCostRv;
        const usedBalanceRv = Big(posCost)
          .plus(fixedUsedRv)
          .plus(totalOrdUsedBalanceRv)
          .plus(totalDebtCostRv)
          .plus(totalOrdOpenLossRv);
        return plus(p, usedBalanceRv);
      },
      '0'
    );
  const totalBonusRv = data.reduce((p, {totalBonusRv = '0'}) => plus(p, totalBonusRv), '0');
  return {
    totalBalanceRv,
    totalUsedBalanceRv,
    totalBonusRv,
    isolatedTotalBalanceRv,
    uid: data && data[0] && data[0].userID,
  };
}

export function convertRiskUnit(data: RiskUnit[]): RiskUnit[] {
  // posSide 为0，riskMode为1说明是cross模式，适用所有仓位
  const posSideMap = {
    0: 'Invalid',
    1: 'Long',
    2: 'Short',
    3: 'Merged',
  };
  return data.map(({posSide, ...item}) => {
    const {
      totalBalanceRv = '0',
      totalPosCostRv = '0',
      totalDebtCostRv = '0',
      totalOrdUsedBalanceRv = '0',
      totalOrdOpenLossRv = '0',
    } = item;

    const totalUsedBalanceRv = toString(
      Big(totalPosCostRv).plus(totalDebtCostRv).plus(totalOrdUsedBalanceRv).plus(totalOrdOpenLossRv)
    );
    return {
      ...item,
      posSide: posSideMap[posSide],
      accountBalanceRv: totalBalanceRv,
      totalUsedBalanceRv,
    };
  });
}

combineLatest([ras$, aopMsgV2$, contractDefaultTakerFeeRateEr$])
  .pipe(throttleTime(300))
  .subscribe(([ras, aop, contractDefaultTakerFeeRateEr]) => {
    const usdtPosition = aop.positions.filter(
      ({currency, isCross}) => contractUtil.isUSDTPerpetualV2(currency) && isCross
    );
    const totalPosCost = calcPosCostV2(usdtPosition, contractDefaultTakerFeeRateEr);
    track('risk_units_debug', {
      ras,
      totalPosCost: toString(totalPosCost),
    });
  });

function calcPosCostV2(positions, defaultTakerFeeEr: number) {
  const items = positions.filter(({leverageRr}) => leverageRr <= 0);
  const symbols = Array.from(new Set(items.map(({symbol}) => symbol)));
  return symbols
    .map((symbol) => {
      const curPositions = items.filter((item) => item.symbol === symbol);
      const isOneWay = curPositions.every(({posMode}) => posMode === 'OneWay');
      if (isOneWay) {
        const position = curPositions.find(({posMode, posSide}) => posMode === 'OneWay' && posSide === 'Merged');
        if (!position) {
          return Big(0);
        }
        const markPrice = position.markPriceRp;
        const {side, size, buyValueToCostRr: buyValueToCostEr, sellValueToCostRr: sellValueToCostEr} = position;
        const markValue = size && markPrice ? Big(size).times(markPrice) : Big(0);
        const valueToCostEr = side === 'Buy' ? buyValueToCostEr : sellValueToCostEr;
        return markValue.times(Big(valueToCostEr).minus(defaultTakerFeeEr));
      } else {
        const posLong = curPositions.find(({posMode, posSide}) => posMode === 'Hedged' && posSide === 'Long');
        const posShort = curPositions.find(({posMode, posSide}) => posMode === 'Hedged' && posSide === 'Short');
        const longSize = posLong?.size || 0;
        const shortSize = posShort?.size || 0;
        const morePosition = longSize > shortSize ? posLong : posShort;
        if (!morePosition) {
          return Big(0);
        }
        const netPartSize = Big(longSize).minus(shortSize).abs();
        const markPrice = posLong.markPriceRp;
        const netPartValue = netPartSize.gt(0) ? Big(netPartSize).times(markPrice) : Big(0);
        const {side, buyValueToCostRr: buyValueToCostEr, sellValueToCostRr: sellValueToCostEr} = morePosition;
        const valueToCostEr = side === 'Buy' ? buyValueToCostEr : sellValueToCostEr;
        return netPartValue.times(Big(valueToCostEr).minus(defaultTakerFeeEr));
      }
    })
    .reduce((p, c) => Big(p).plus(c), Big(0));
}
